import { computed } from 'vue';
import { difference } from 'lodash';
import { useI18n, useUrl, useMainMenuComplexTabsMeta, useTableQuery } from '@/composables';
import { useStore } from '@/store';

export interface TabData {
  name: string;
  title: string;
  icon?: string;
  hideInMenu?: boolean;
  isLive?: boolean;
  meta?: any;
  showCondition?: () => boolean;
  isActive?: boolean;
}

export const useMainMenuComplexTabs = () => {
  const { t } = useI18n();
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const { customKeys, tabsMeta } = useMainMenuComplexTabsMeta();
  const {
    computedType,
    isReport,
    isHidden,
    getLink,
    getLivesLink,
  } = useUrl();

  const tableDrilldownFilter = computed(
    () => tableQueryApi.tableDrilldownFilter.value,
  );

  const campaignAsFirstTableFilterCondition = computed(
    () =>
      tableDrilldownFilter.value[0]
      && tableDrilldownFilter.value[0].filterTables === 'campaigns',
  );

  const trafficSourcesAsFirstTableFilterCondition = computed(
    () =>
      tableDrilldownFilter.value[0]
      && tableDrilldownFilter.value[0].filterTables === 'traffic-sources',
  );

  const customGroupsShowCondition = computed(() => true);

  const customGroupsData = computed(() =>
    customKeys.value.map((key, index) => ({
      name: 'custom' + (index + 1),
      title: t('custom {0}', [index + 1]),
      meta: {
        customValue:
          tableQueryApi.tableCustomGroups.value[key]
            ? tableQueryApi.tableCustomGroups.value[key]
            : t('n/a'),
      },
      showCondition: () => customGroupsShowCondition.value,
    })),
  );

  const disableTreeViewList = ['ips', 'useragents', 'referrers'];

  const isTreeLevelsAllowed = computed(
    () => !disableTreeViewList.includes(computedType.value),
  );

  const reportingFlowRulePathEnabled = computed(
    () => store.user.featureFlags?.reportingFlowRulePath?.enabled,
  );

  const pComplexTabsData = computed((): { [type: string]: TabData[] } => ({
    mainPlain: [
      {
        name: 'campaigns',
        icon: tabsMeta.value['campaigns'].icon,
        title: tabsMeta.value['campaigns'].title,
      },
      {
        name: 'offers',
        icon: tabsMeta.value['offers'].icon,
        title: tabsMeta.value['offers'].title,
      },
      {
        name: 'landings',
        icon: tabsMeta.value['landings'].icon,
        title: tabsMeta.value['landings'].title,
      },
      {
        name: 'flows',
        icon: tabsMeta.value['flows'].icon,
        title: tabsMeta.value['flows'].title,
      },
      {
        name: 'traffic-sources',
        icon: tabsMeta.value['traffic-sources'].icon,
        title: tabsMeta.value['traffic-sources'].title,
      },
      {
        name: 'affiliate-networks',
        icon: tabsMeta.value['affiliate-networks'].icon,
        title: tabsMeta.value['affiliate-networks'].title,
      },
    ],
    flow_rules_paths: [
      {
        name: 'flow_rule_paths',
        icon: tabsMeta.value['conversions'].icon,
        title: tabsMeta.value['flow_rule_paths'].title,
        showCondition: () =>
          isReport.value && reportingFlowRulePathEnabled.value,
      },
      {
        name: 'flow_rules',
        icon: tabsMeta.value['conversions'].icon,
        title: tabsMeta.value['flow_rules'].title,
        showCondition: () =>
          isReport.value && reportingFlowRulePathEnabled.value,
      },
    ],
    conversions: [
      {
        name: 'conversions',
        icon: tabsMeta.value['conversions'].icon,
        title: tabsMeta.value['conversions'].title,
        showCondition: () => !tableQueryApi.tableIsHideFilters.value,
      },
    ],
    postbacks: [
      {
        name: 'postbacks',
        icon: tabsMeta.value['postbacks'].icon,
        title: t('Postbacks'),
        showCondition: () =>
          (isReport.value
            && !tableQueryApi.tableIsHideFilters.value
            && campaignAsFirstTableFilterCondition.value
            && tableDrilldownFilter.value.length <= 1)
          || !isReport.value,
      },
    ],
    errors: [
      {
        name: 'errors',
        icon: tabsMeta.value['errors'].icon,
        title: t('Errors'),
        showCondition: () =>
          (isReport.value
            && !tableQueryApi.tableIsHideFilters.value
            && campaignAsFirstTableFilterCondition.value
            && tableDrilldownFilter.value.length <= 1)
          || !isReport.value,
      },
    ],
    browsers: [
      {
        name: 'default',
        title: t('Browsers'),
        hideInMenu: true,
      },
      {
        name: 'browsers',
        title: t('Browser Names'),
      },
      {
        name: 'browser-versions',
        title: t('Browser Versions'),
      },
      {
        name: 'useragents',
        title: t('User Agents'),
        showCondition: () =>
          isReport.value && campaignAsFirstTableFilterCondition.value,
      },
    ],
    oses: [
      {
        name: 'oses',
        title: t('OS'),
      },
      {
        name: 'os-versions',
        title: t('OS Versions'),
      },
    ],
    devices: [
      {
        name: 'default',
        title: t('Devices'),
        hideInMenu: true,
      },
      {
        name: 'device-models',
        title: t('Device Models'),
      },
      {
        name: 'device-vendors',
        title: t('Device Vendors'),
      },
      {
        name: 'device-types',
        title: t('Device Types'),
      },
      {
        name: 'screen-resolutions',
        title: t('Screen Resolutions'),
      },
    ],
    isps: [
      {
        name: 'isps',
        title: t('ISP'),
      },
      {
        name: 'mobile-carriers',
        title: t('Mobile Carriers'),
      },
      {
        name: 'connection-types',
        title: t('Connection Types'),
      },
    ],
    countries: [
      {
        name: 'countries',
        title: t('Countries'),
        hideInMenu: true,
      },
      {
        name: 'languages',
        title: t('Languages'),
      },
      {
        name: 'cities',
        title: t('Cities'),
      },
      {
        name: 'states',
        title: t('State / Region'),
      },
      {
        name: 'countries',
        title: t('Countries'),
      },
    ],
    lives: [
      {
        name: 'live-visits',
        title: t('Live Visits'),
        isLive: true,
        showCondition: () =>
          isReport.value && campaignAsFirstTableFilterCondition.value,
      },
      {
        name: 'live-clicks',
        title: t('Live Clicks'),
        isLive: true,
        showCondition: () =>
          isReport.value && campaignAsFirstTableFilterCondition.value,
      },
    ],
    referrers: [
      {
        name: 'referrers',
        title: t('Referrers'),
        showCondition: () =>
          isReport.value && campaignAsFirstTableFilterCondition.value,
      },
      {
        name: 'referrer-domains',
        title: t('Referrer Domains'),
        showCondition: () => isReport.value,
      },
    ],
    ips: [
      {
        name: 'default',
        title: t('IPs'),
        hideInMenu: true,
        showCondition: () =>
          !campaignAsFirstTableFilterCondition.value && isReport.value,
      },
      {
        name: 'ips',
        title: t('IPs'),
        showCondition: () =>
          campaignAsFirstTableFilterCondition.value && isReport.value,
      },
      {
        name: 'subnet-small',
        title: t('Subnets (Small)'),
        showCondition: () => isReport.value,
      },
      {
        name: 'subnet-big',
        title: t('Subnets (Big)'),
        showCondition: () => isReport.value,
      },
    ],
    period: [
      {
        name: 'default',
        title: t('Period'),
        hideInMenu: true,
        showCondition: () => isReport.value,
      },
      {
        name: 'month',
        title: t('Month'),
        showCondition: () => isReport.value,
      },
      {
        name: 'day',
        title: t('Day of Month'),
        showCondition: () => isReport.value,
      },
      {
        name: 'day-of-week',
        title: t('Day Of Week'),
        showCondition: () => isReport.value,
      },
      {
        name: 'hour-of-day',
        title: t('Hour Of Day'),
        showCondition: () => isReport.value,
      },
    ],
    custom: customGroupsData.value,
  }));

  const complexTabsData = computed((): { [type: string]: TabData[] } => {
    const keys = Object.keys(pComplexTabsData.value);
    const result: { [type: string]: TabData[] } = {};

    for (const key of keys) {
      const tabs = pComplexTabsData.value[key].filter(
        (tab) =>
          (!tab.showCondition || tab.showCondition()) && !isHidden(tab.name),
      );
      if (tabs.length) {
        result[key] = tabs;
      }
    }

    return result;
  });

  const getTabsPlainTabsData = (
    complexData: { [type: string]: TabData[] },
  ): { [type: string]: TabData } => {
    const keys = Object.keys(complexData);
    const data: TabData[] = [];

    for (const key of keys) {
      const actualData = complexData[key].filter((item) => !item.hideInMenu);
      data.push(...actualData);
    }

    const result: { [type: string]: TabData } = {};
    data.forEach((item) => {
      result[item.name] = item;
    });

    return result;
  };

  const tabsPlainTabsData = computed(() =>
    getTabsPlainTabsData(complexTabsData.value),
  );

  const tabsPlainTabsDataBase = computed(() =>
    getTabsPlainTabsData(pComplexTabsData.value),
  );

  const tabsPlainTabsDataExclude = computed(() => {
    const keys = Object.keys(tabsPlainTabsData.value);
    const keysBase = Object.keys(tabsPlainTabsDataBase.value);
    return difference(keysBase, keys);
  });

  const title = computed(() => {
    const tabData = tabsPlainTabsData.value[computedType.value];
    if (tabData) {
      return tabData.title;
    }
    return '';
  });

  const getTab = (tabName: string) => {
    if (!complexTabsData.value[tabName]) {
      return null;
    }

    let tab: TabData | undefined = complexTabsData.value[tabName].find(
      (item) => item.name === computedType.value,
    );

    if (!tab) {
      tab = { ...complexTabsData.value[tabName][0] };
      tab.isActive = false;
    } else {
      tab = { ...tab, isActive: true };
    }

    return tab;
  };

  const complexTabsBase = computed(() => {
    const drilldownFilter = tableQueryApi.tableDrilldownFilter.value;
    const campaignAsFirstTableFilter =
      drilldownFilter
      && drilldownFilter.length
      && drilldownFilter[0].filterTables === 'campaigns';

    return [
      {
        name: 'countries',
        icon: tabsMeta.value['countries'].icon,
        data: getTab('countries'),
      },
      {
        name: 'isps',
        icon: tabsMeta.value['isps'].icon,
        data: getTab('isps'),
      },
      {
        name: 'ips',
        icon: tabsMeta.value['ips'].icon,
        data: getTab('ips'),
        showCondition: () => campaignAsFirstTableFilter,
      },
      {
        name: 'devices',
        icon: tabsMeta.value['devices'].icon,
        data: getTab('devices'),
      },
      {
        name: 'oses',
        icon: tabsMeta.value['oses'].icon,
        data: getTab('oses'),
      },
      {
        name: 'browsers',
        icon: tabsMeta.value['browsers'].icon,
        data: getTab('browsers'),
      },
      {
        name: 'referrers',
        icon: tabsMeta.value['referrers'].icon,
        data: getTab('referrers'),
        showCondition: () => isReport.value,
      },
      {
        name: 'lives',
        icon: tabsMeta.value['lives'].icon,
        data: getTab('lives'),
        showCondition: () => isReport.value,
      },
      {
        name: 'flow_rules_paths',
        icon: tabsMeta.value['flows'].icon,
        data: getTab('flow_rules_paths'),
        showCondition: () => isReport.value,
      },
      {
        name: 'period',
        icon: tabsMeta.value['period'].icon,
        data: getTab('period'),
        showCondition: () => isReport.value,
      },
    ];
  });

  const complexTabs = computed(() =>
    complexTabsBase.value.filter((tab) => tab.data),
  );

  return {
    tabsMeta,
    customKeys,
    tableDrilldownFilter,
    campaignAsFirstTableFilterCondition,
    customGroupsShowCondition,
    isTreeLevelsAllowed,
    complexTabsData,
    tabsPlainTabsData,
    tabsPlainTabsDataBase,
    tabsPlainTabsDataExclude,
    title,
    getTab,
    complexTabs,
    getLink,
    getLivesLink,
    isReport,
    isHidden,
    computedType,
  };
};
